import type { AuthProvider } from "@refinedev/core";
import { supabase, REFRESH_TOKEN_KEY } from "@/providers/supabaseClient";
import type { AdminIdentity, AdminRole } from "@/providers/types";
import { ROLE_HOME } from "@/providers/types";

const VALID_ROLES = new Set<string>(["support", "moderator", "superadmin", "analyst"]);

function parseRole(raw: unknown): AdminRole | null {
  if (typeof raw === "string" && VALID_ROLES.has(raw)) {
    return raw as AdminRole;
  }
  return null;
}

export const authProvider: AuthProvider = {
  login: async ({ email, password }: { email: string; password: string }) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error || !data.session) {
      return {
        success: false,
        error: {
          name: "LoginError",
          message: error?.message ?? "Неверный email или пароль",
        },
      };
    }

    const rawRole = data.session.user.app_metadata?.["role"];
    const role = parseRole(rawRole);

    if (!role) {
      console.warn("[authProvider] login: invalid role claim in JWT:", rawRole);
      return {
        success: false,
        error: {
          name: "LoginError",
          message: "Недостаточно прав для входа в панель",
        },
      };
    }

    sessionStorage.setItem(REFRESH_TOKEN_KEY, data.session.refresh_token);

    const redirectTo = ROLE_HOME[role] ?? "/users";

    return { success: true, redirectTo };
  },

  logout: async () => {
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    await supabase.auth.signOut();
    return { success: true, redirectTo: "/login" };
  },

  check: async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (session) {
      return { authenticated: true };
    }

    const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      return { authenticated: false, redirectTo: "/login" };
    }

    const { data, error } = await supabase.auth.refreshSession({
      refresh_token: refreshToken,
    });

    if (error || !data.session) {
      sessionStorage.removeItem(REFRESH_TOKEN_KEY);
      return { authenticated: false, redirectTo: "/login" };
    }

    sessionStorage.setItem(REFRESH_TOKEN_KEY, data.session.refresh_token);
    return { authenticated: true };
  },

  onError: async (error: { status?: number; statusCode?: number; code?: string }) => {
    const httpStatus = error?.status ?? error?.statusCode;
    if (httpStatus === 401 || error?.code === "auth.unauthorized") {
      return { logout: true, redirectTo: "/login" };
    }
    return { logout: false };
  },

  getIdentity: async (): Promise<AdminIdentity | null> => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) return null;

    const role = parseRole(user.app_metadata?.["role"]);
    if (!role) {
      console.warn("[authProvider] getIdentity: invalid role claim in JWT:", user.app_metadata?.["role"]);
      return null;
    }

    return {
      id: user.id,
      email: user.email,
      role,
    };
  },

  getPermissions: async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) return null;
    return parseRole(user.app_metadata?.["role"]);
  },
};
